import { Pagination } from './common';
import { Customer } from './customer';

export type DealerInfo = {
  companyCode: string;
  customerNumber: string;
};

export type ProductQuery = {
  customerId?: string;
  languageCode?: string;
  pagination?: Pagination;
  iprId?: string | null;
  husqvarnaId?: string | null;
  pnc?: string | null;
  serialNumber?: string | null;
};

export type Product = {
  brand: string;
  description?: string;
  imageId: string | null;
  imageUrl: string | null;
  iprId: string;
  modelName: string;
  productno: string;
  serialNumber: string;
  pnc: string;
  productCategory: string;
  husqvarnaId: string;
};

export type DealerAddress = {
  coAddress: string;
  name: string;
  street: string;
  zipCity: string;
};

type DocumentPublishTitle = {
  en: string;
};
export type ProductDocumentData = {
  id: string;
  format: string;
  iprId: string;
  languages: string[];
  link: string;
  publicationTitle: DocumentPublishTitle;
  publicationType: string;
  sizeBytes: number;
};

export type DealerData = {
  address: DealerAddress;
  iprId: string;
  name: string;
};

export type Dealer = {
  iprId: string | null;
  name: string | null;
  address: DealerAddress | null;
  companyCode: string | null;
  customerNumber: string | null;
};

export type TermsAndConditions = {
  id: string;
  type: string;
  attributes: {
    publicationType: string;
    publicationTitle: Record<string, string>;
    publicationId: string;
    created: number;
    updated: number;
    binaryLastModified: number;
    languages: string[];
    publishedBy: {
      status: string;
      startTime: number;
    };
    format: string;
    link: string;
    sizeBytes: number;
    documentArticleNumber: string;
    originalFilename: string;
    filename: string;
    intendedCountries: Record<string, string>;
    intendedCountriesType: string;
    checksum: string;
  };
};

export type ServiceContract = {
  id: string;
  type: string;
  iprId: string;
  customerId: string;
  startDate: number;
  endDate: number;
  status: ContractStatus;
  serviceContractProduct: string;
  marketName?: string;
  termsAndConditions: TermsAndConditions[];
};

export enum ContractStatus {
  Pending = 'PENDING',
  Active = 'ACTIVE',
  OnHold = 'ON_HOLD',
  NeverActivated = 'NEVER_ACTIVATED',
  Cancelled = 'CANCELLED',
  Expired = 'EXPIRED'
}

export type Warranty = {
  expireDate: string;
  extendedWarrantyPeriod: number;
  iprId: string;
  productNo: string;
  serialNo: string;
  soldDate: string;
  source: string;
  verified: boolean;
  verifiedBy: string;
  verifiedWhen: string;
  warrantyPeriod: number;
};

export type DoneBy = {
  userId: string;
  sourceSystem: string;
  time: number;
};

export type Workshop = {
  workshopName: string;
  idType: string;
};

export type ServicedPart = {
  genericName: string;
};

export type PerformedTask = {
  id: string;
  type: string;
  attributes: {
    description: string;
    type: string;
    servicedParts: ServicedPart[];
    createdBy: DoneBy;
    modifiedBy: DoneBy;
  };
};

export type ServiceRecord = {
  id: string;
  title: string;
  description: string;
  recordDate: number;
  performedByType: string;
  type: string;
  workshop: Workshop;
  createdBy: DoneBy;
  relationships: {
    performedTasks: {
      data: PerformedTask[];
    };
  };
};

export interface ProductDetails {
  product: Product;
  dealer: DealerData | null;
  serviceContracts: ServiceContract[];
  documents: ProductDocumentData[];
  warranty: Warranty | null;
  serviceRecords: ServiceRecord[];
  hasOffers: boolean;
}

export type Registration = {
  id: string;
  productId: string;
  customerId: string;
  iprId: string;
  ownerType: string;
  relationshipType: string;
  dataReliability: string;
  productType: string;
  createdForSystem: string;
  created: number;
  dealer: DealerData;
};

export type ProductRegistration = {
  customer: Customer;
  product: Product;
  warranty: Warranty | null;
  registrations: Registration[];
  serviceContracts: ServiceContract[];
  dealers: Dealer[];
};

export interface ProductRegistrationList {
  productRegistrations: ProductRegistration[];
  nextCursor: string;
}

export type ProductDetailsContext = {
  data: ProductDetails;
};

export type ProductOfferings = {
  iprId: string;
  offerings: boolean;
};

type LiteralUnion<T extends string> = T | (string & Record<never, never>);

export type IdentifierValidationStatus = LiteralUnion<'VALID' | 'INVALID'>;
export type IdentifierVerificationStatus = LiteralUnion<'SUCCESS' | 'FAILED'>;
export type IdentifierRegistrationStatus = LiteralUnion<
  'REGISTERED' | 'NOT_REGISTERED'
>;

export type IdentifierValidationError = {
  code: string;
  message: string;
};

export type IdentifierValidationData = {
  status?: IdentifierValidationStatus;
  extracted?: Record<string, string>;
  derived?: Record<string, string>;
  errors?: IdentifierValidationError[];
};

export type IdentifierVerification = {
  status?: IdentifierVerificationStatus;
  errors?: IdentifierValidationError[];
  [key: string]: unknown;
};

export type IdentifierValidationProfile = {
  profile: string;
  validation: IdentifierValidationData;
  verification?: IdentifierVerification[];
};

export type IdentifierRegistration = {
  status: IdentifierRegistrationStatus;
  iprId?: string;
  errors?: IdentifierValidationError[];
};

export type IdentifierValidationResult = {
  type: string;
  id: string;
  attributes: {
    profiles: IdentifierValidationProfile[];
    registration?: IdentifierRegistration;
  };
};

export type IdentifierValidationResponse = {
  data: IdentifierValidationResult[];
};
